import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, PageHeader } from '../../components/ui';
import api from '../../services/api';
import toast from 'react-hot-toast';

// Decode MIME encoded subjects like =?UTF-8?Q?Re=3A_Hello?= → Re: Hello
const decodeMime = (str) => {
  if (!str) return '';
  return str.replace(/=\?([^?]+)\?([BQ])\?([^?]*)\?=/gi, (_, charset, enc, text) => {
    try {
      if (enc.toUpperCase() === 'Q') {
        // Quoted-printable: replace _ with space, decode =XX hex sequences
        const bytes = text.replace(/_/g, ' ').replace(/=([0-9A-F]{2})/gi, (m, h) => String.fromCharCode(parseInt(h, 16)));
        // Properly decode UTF-8 byte string
        return decodeURIComponent(bytes.split('').map(c => '%' + c.charCodeAt(0).toString(16).padStart(2,'0')).join(''));
      } else {
        // Base64: decode then handle UTF-8
        const binary = atob(text);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return new TextDecoder('utf-8').decode(bytes);
      }
    } catch { return text; }
  });
};

// Clean raw MIME email body — strips boundaries, headers, quoted text, decodes QP
const cleanReplyBody = (raw) => {
  if (!raw) return '';

  // Decode quoted-printable =XX sequences and soft line breaks
  const decodeQP = (str) => str
    .replace(/=\r?\n/g, '')  // soft line breaks
    .replace(/=([0-9A-F]{2})/gi, (_, h) => {
      try {
        return decodeURIComponent('%' + h);
      } catch { return String.fromCharCode(parseInt(h, 16)); }
    });

  // Extract only the plain text part (before HTML part or quoted text)
  let text = raw;

  // If multipart, grab content between first boundary sections
  const boundaryMatch = raw.match(/boundary="?([^"\s;]+)"?/i);
  if (boundaryMatch) {
    const boundary = boundaryMatch[1];
    const parts = raw.split(new RegExp('--' + boundary.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')));
    // Find the text/plain part
    for (const part of parts) {
      if (part.includes('Content-Type: text/plain')) {
        // Strip headers from part
        const bodyStart = part.indexOf('\n\n');
        if (bodyStart !== -1) {
          text = part.slice(bodyStart + 2);
          break;
        }
      }
    }
  }

  // Decode quoted-printable
  text = decodeQP(text);

  // Remove MIME headers lines
  text = text.replace(/^Content-[^\n]+\n/gmi, '');
  text = text.replace(/^--[a-zA-Z0-9_\-]+/gm, '');

  // Remove quoted original email (lines starting with >)
  const lines = text.split('\n');
  const replyLines = [];
  for (const line of lines) {
    if (line.trim().startsWith('>')) break; // stop at quoted text
    if (line.match(/^On .+ wrote:$/)) break; // stop at "On ... wrote:"
    replyLines.push(line);
  }
  text = replyLines.join('\n');

  // Clean up excessive whitespace
  return text.replace(/\n{3,}/g, '\n\n').trim();
};

export default function Inbox() {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  useEffect(() => { fetchReplies(); }, []);

  const fetchReplies = async () => {
    try { const r = await api.get('/inbox'); setReplies(r.data.data || []); }
    catch {} finally { setLoading(false); }
  };

  const markRead = async (id) => {
    try {
      await api.put(`/inbox/${id}/read`);
      setReplies(prev => prev.map(r => r.id === id ? { ...r, isRead: true } : r));
    } catch {}
  };

  const filtered = filter === 'unread' ? replies.filter(r => r.isRead === false || r.isRead === null) : replies;

  return (
    <div>
      <PageHeader title="Inbox" subtitle={`${replies.filter(r => r.isRead === false || r.isRead === null).length} unread replies`} />

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['all','unread'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ padding: '7px 18px', borderRadius: 8, border: `1px solid ${filter===f ? '#2ECC71' : 'rgba(46,204,113,0.15)'}`, background: filter===f ? 'rgba(46,204,113,0.12)' : 'transparent', color: filter===f ? '#2ECC71' : '#666', fontWeight: filter===f ? 700 : 400, fontSize: 13, cursor: 'pointer', textTransform: 'capitalize' }}>
            {f === 'unread' ? `Unread (${replies.filter(r => r.isRead === false || r.isRead === null).length})` : 'All Replies'}
          </button>
        ))}
      </div>

      {loading ? <div style={{ color:'#555', textAlign:'center', padding:40 }}>Loading...</div> : (
        filtered.length === 0 ? (
          <Card style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📬</div>
            <h3 style={{ color: '#ccc', marginBottom: 8 }}>No replies yet</h3>
            <p style={{ color: '#555', fontSize: 14 }}>Replies from your campaigns will appear here.</p>
          </Card>
        ) : (
          <div style={{ display: 'flex', gap: 16 }}>
            {/* Reply list */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.map(r => (
                <Card key={r.id} onClick={() => { setSelected(r); if (!r.isRead) markRead(r.id); }}
                  style={{ cursor: 'pointer', borderColor: selected?.id === r.id ? '#2ECC71' : (r.isRead === false || r.isRead === null) ? 'rgba(46,204,113,0.3)' : 'rgba(46,204,113,0.08)', background: (r.isRead === false || r.isRead === null) ? 'rgba(46,204,113,0.04)' : '#111', padding: '14px 18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div style={{ fontWeight: (!r.isRead && r.isRead !== false) || r.isRead === false ? 700 : 400, fontSize: 14, color: '#fff' }}>
                      {decodeMime(r.subject) ? decodeMime(r.subject).replace(/=\?[^?]+\?[BQ]\?[^?]*\?=/gi, s => { try { return decodeURIComponent(s.replace(/=\?UTF-8\?Q\?/i,'').replace(/\?=$/,'').replace(/_/g,' ').replace(/=([0-9A-F]{2})/gi,(_,h)=>String.fromCharCode(parseInt(h,16)))); } catch { return s; } }) : '(No subject)'}
                    </div>
                    {(r.isRead === false || r.isRead === null) && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#2ECC71', flexShrink: 0, marginTop: 4 }} />}
                  </div>
                  <div style={{ fontSize: 12, color: '#555' }}>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</div>
                </Card>
              ))}
            </div>

            {/* Preview panel */}
            {selected && (
              <div style={{ width: 380, flexShrink: 0 }}>
                <Card>
                  <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 15 }}>{decodeMime(selected.subject) || '(No subject)'}</div>
                  <div style={{ fontSize: 12, color: '#555', marginBottom: 16 }}>{selected.createdAt ? new Date(selected.createdAt).toLocaleString() : ''}</div>
                  <div style={{ fontSize: 14, color: '#ccc', lineHeight: 1.7, background: '#1a1a1a', borderRadius: 10, padding: 16, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {selected.replyBody ? cleanReplyBody(selected.replyBody) || selected.replyBody.replace(/<[^>]+>/g,'').trim() : 'Reply body not available — make sure IMAP is configured on your email account.'}
                  </div>
                  <Button size="sm" style={{ marginTop: 16 }} fullWidth>↩ Reply</Button>
                </Card>
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}
